import { useMemo } from 'react';
import { useLatest } from 'react-use';
import pDebounce from 'p-debounce';

import { SnackbarFunctionProps, useSnackbar } from '@lyearn/ui';
import useDefaultErrorSnackbar from '@/hooks/useDefaultErrorSnackbar';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { ArticleContentType, UpdateArticleInput } from '@/types/schema';

import { useUpdateArticleMutation } from '../graphql/mutation/generated/UpdateArticle';
import getTransformedArticle from '../utils/getTransformedArticle';
import { ArticleUIState } from '../views/ArticleBuilder/context';

const useUpdateArticle = (
  article?: ArticleContentType,
  articleId?: string,
  setUiState?: React.Dispatch<React.SetStateAction<ArticleUIState>>,
) => {
  const [, updateArticle] = useUpdateArticleMutation();
  const { showDefaultErrorSnackbar } = useDefaultErrorSnackbar();
  const user = useUserContext();
  const currentSubWorkspace = user?.currentSubWorkspace;
  const articleMutationInput = useLatest(
    getTransformedArticle(article as ArticleContentType, currentSubWorkspace),
  );
  const { showSnackbar } = useSnackbar();

  const onUpdateArticle = useMemo(
    () =>
      pDebounce(async (articleData?: UpdateArticleInput, snackbarProps?: SnackbarFunctionProps) => {
        const updateArticleData = await updateArticle({
          article: {
            ...articleMutationInput.current,
            articleId,
            ...articleData,
          },
        });
        if (updateArticleData.data?.updateArticle.errorCode) {
          showDefaultErrorSnackbar();
        } else if (snackbarProps) {
          showSnackbar(snackbarProps);
        }
        if (!updateArticleData.data?.updateArticle.article) {
          setUiState?.((prevState) => {
            return {
              ...prevState,
              isOfSameWorkspace: false,
            };
          });
        }
      }, 400),
    [
      articleId,
      articleMutationInput,
      setUiState,
      showDefaultErrorSnackbar,
      showSnackbar,
      updateArticle,
    ],
  );

  return { onUpdateArticle };
};

export default useUpdateArticle;
